import React, { useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertTriangle,
  ChevronRight,
  Bell,
  CreditCard,
  HelpCircle,
  Users,
  FileWarning,
  ArrowRightLeft,
  Receipt,
  Copy,
  MapPin,
} from 'lucide-react';
import { useDataNotifications } from '@/hooks/useDataNotifications';
import { useCompanyInfo } from '@/hooks/useCompanyInfo';
import { refreshDataGapNotifications } from '@/lib/dataGapNotifications';
import type { DataNotificationType } from '@/integrations/supabase/payrollNotificationsTypes';

interface TypeSummary {
  type: DataNotificationType;
  count: number;
  label: string;
  description: string;
  icon: React.ReactNode;
  actionLabel: string;
  route?: string;
}

const TYPE_META: Record<DataNotificationType, {
  label: string;
  description: string;
  icon: React.ReactNode;
  actionLabel: string;
  route?: string;
}> = {
  pos_mismatch: {
    label: 'POS Payout Mismatches',
    description: 'POS payouts with no matching bank deposit — affects sales accuracy and cash timing.',
    icon: <ArrowRightLeft className="h-4 w-4" />,
    actionLabel: 'Reconcile',
    route: '/dashboard/accounting/reconciliation',
  },
  unrecognized_expense: {
    label: 'Unrecognized Expenses',
    description: 'Bank transactions that could not be automatically categorized.',
    icon: <HelpCircle className="h-4 w-4" />,
    actionLabel: 'Identify expenses',
  },
  payroll_clarification: {
    label: 'Payroll Clarifications',
    description: 'Payroll transactions that need location assignment and type classification.',
    icon: <Users className="h-4 w-4" />,
    actionLabel: 'Assign payroll',
  },
  vendor_mapping: {
    label: 'Vendor Mapping',
    description: 'Transactions with unrecognized vendors that need to be mapped.',
    icon: <Receipt className="h-4 w-4" />,
    actionLabel: 'Map vendors',
  },
  missing_invoice: {
    label: 'Missing Invoices',
    description: 'Expenses that are missing supporting invoices or receipts.',
    icon: <FileWarning className="h-4 w-4" />,
    actionLabel: 'Attach invoices',
  },
  large_transaction: {
    label: 'Large Transactions',
    description: 'Unusually large transactions that may need review.',
    icon: <CreditCard className="h-4 w-4" />,
    actionLabel: 'Review transactions',
  },
  duplicate_vendor: {
    label: 'Duplicate Vendors',
    description: 'Potential duplicate vendor entries that should be merged.',
    icon: <Copy className="h-4 w-4" />,
    actionLabel: 'Merge vendors',
  },
  location_allocation: {
    label: 'Location Allocations',
    description: 'Transactions that need to be allocated across locations.',
    icon: <MapPin className="h-4 w-4" />,
    actionLabel: 'Allocate transactions',
  },
};

export function ActionNeededModule() {
  const navigate = useNavigate();
  const { data: company } = useCompanyInfo();
  const {
    data: notifications,
    isLoading,
    refetch,
  } = useDataNotifications();

  useEffect(() => {
    if (!company?.id) return;
    refreshDataGapNotifications(company.id)
      .then(() => refetch())
      .catch((err) => console.error('refreshDataGapNotifications failed:', err));
  }, [company?.id, refetch]);

  const typeSummaries = useMemo<TypeSummary[]>(() => {
    const openNotifs = (notifications ?? []).filter((n) => n.status === 'open');
    const grouped = new Map<DataNotificationType, number>();
    for (const n of openNotifs) {
      grouped.set(n.type, (grouped.get(n.type) ?? 0) + 1);
    }

    return Array.from(grouped.entries())
      .map(([type, count]) => ({
        type,
        count,
        ...TYPE_META[type],
      }))
      .sort((a, b) => b.count - a.count);
  }, [notifications]);

  const totalOpen = typeSummaries.reduce((sum, s) => sum + s.count, 0);

  if (isLoading) {
    return null;
  }

  const hasNotifications = totalOpen > 0;

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-muted/30 to-background">
      <CardContent className="p-6">
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Bell className="h-5 w-5 text-primary" />
              Action Needed
              {hasNotifications && (
                <Badge variant="secondary" className="ml-1 text-xs font-medium">
                  {totalOpen}
                </Badge>
              )}
            </h3>
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/notifications')}>
              View All
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>

          {hasNotifications ? (
            <div className="space-y-3">
              {typeSummaries.map((summary) => (
                <TypeSummaryCard
                  key={summary.type}
                  summary={summary}
                  onAction={() => navigate(summary.route ?? `/dashboard/notifications?type=${summary.type}`)}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No system discrepancies to resolve. When we detect issues (e.g. POS mismatches, unrecognized expenses, payroll clarifications), they&apos;ll appear here for you to review.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function TypeSummaryCard({ summary, onAction }: { summary: TypeSummary; onAction: () => void }) {
  return (
    <div className="flex items-center gap-4 rounded-lg border bg-background/80 p-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
        {summary.icon}
      </div>
      <div className="flex-1 min-w-0 space-y-0.5">
        <div className="flex items-center gap-2">
          <p className="font-medium text-sm">{summary.label}</p>
          <Badge variant="outline" className="text-xs tabular-nums">
            {summary.count}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">{summary.description}</p>
      </div>
      <Button size="sm" onClick={onAction} className="shrink-0">
        {summary.actionLabel}
        <ChevronRight className="h-3.5 w-3.5 ml-1" />
      </Button>
    </div>
  );
}
